import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  Radio,
  Typography
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { EditionHeader } from "./EditionHeader.component";
import { getModalEditionChangeStyles } from "./ModalEditionChange.style";
import { SessionItem } from "./SessionItem.component";
import usersRoundOrange from "../../assets/users-round-orange.svg";
import usersRound from "../../assets/users-round.svg";
import { capitalizeString, formatDateWithCapitalizedMonth } from "../../queries";
import { EditionWithSessions, SessionWithTeachersAndTutors } from "../../types.d";
import { Icon } from "../Icon";

interface EditionCardProps {
  editionWithSessions: EditionWithSessions;
  isVirtual: boolean;
  disabled: boolean;
  isExpanded: boolean;
  onToggleAccordion: () => void;
  theme: Theme;
  t: TFunction;
}

interface AvailabilityInfoProps {
  hasAvailablePlaces: boolean;
  remainingPlaces: number;
  totalPlaces: number;
  theme: Theme;
  t: TFunction;
}

const AvailabilityInfo = ({ hasAvailablePlaces, remainingPlaces, totalPlaces, theme, t }: AvailabilityInfoProps) => (
  <Box sx={ { alignItems: "center", display: "flex" } }>
    { hasAvailablePlaces ? (
      <>
        <img src={ usersRound } alt="users-logo-icon" width={ 16 } height={ 16 } style={ { marginRight: "8px" } } />
        <Typography sx={ { color: theme.customColors.accentVariantA, fontSize: "0.75rem" } }>
          { remainingPlaces }/{ totalPlaces } { t("edition_remaining_places") }
        </Typography>
      </>
    ) : (
      <>
        <img src={ usersRoundOrange } alt="users-logo-icon" width={ 16 } height={ 16 } style={ { marginRight: "8px" } } />
        <Typography sx={ { color: theme.customColors.accentVariantC, fontSize: "0.75rem" } }>
          { t("no_available_places") }
        </Typography>
      </>
    ) }
  </Box>
);


export function EditionCard({   
  editionWithSessions,
  isVirtual,
  disabled,
  isExpanded,
  onToggleAccordion,
  theme,
  t }: EditionCardProps) {
  const sessions = editionWithSessions.sessions || [];
  const editionTitle = editionWithSessions.editionName ||`${t("edition_name")} ${editionWithSessions.id}`;
  const hasMultipleSessions = sessions.length > 1;
  const hasAvailablePlaces = editionWithSessions.remainingPlaces > 0;

  const styles = getModalEditionChangeStyles(theme);

  const getEditionDateRange = (
    editionWithSessions: EditionWithSessions,
    t: TFunction
  ): string => {
    const editionStartTime = dayjs(editionWithSessions.startTime);
    const editionEndTime = dayjs(editionWithSessions.endTime);
  
    if (!editionStartTime || !editionEndTime) return "-";
  
    if (editionStartTime.format("DD/MM/YYYY") === editionEndTime.format("DD/MM/YYYY")) return formatDateWithCapitalizedMonth(editionStartTime);
  
    return `${capitalizeString(t("from"))} ${formatDateWithCapitalizedMonth(editionStartTime)} ${t("to")} ${formatDateWithCapitalizedMonth(editionEndTime)}`;
  };

  const dateRange = getEditionDateRange(editionWithSessions, t);

  return (
    <Box
      sx={ { ...styles.editionCard.cardBox, opacity: disabled ? 0.5 : 1 } }
    >
      <Accordion
        expanded={ isExpanded }
        disableGutters
        onChange={ onToggleAccordion }
        sx={ styles.editionCard.cardAccordion }
      >
        <AccordionSummary
          expandIcon={ <Icon icon="dropdown" size={ 16 } /> }
          aria-controls={ `edition-${editionWithSessions.id}-content` }
          id={ `edition-${editionWithSessions.id}-header` }
          sx={ { pl: 0 } }
        >
          <Box sx={ { alignItems: "center", display: "flex", pl: 0, pr: 2, width: "100%" } }>
            <Box sx={ { alignItems: "center", display: "flex", flex: 1 } }>
              <FormControlLabel
                value={ editionWithSessions.id }
                disabled={ disabled }
                control={
                  <Radio
                    sx={ styles.editionCard.cardRadio }
                  />
                }
                label=""
                sx={ { m: 0, mx: 1 } }
              />
              <Box>
                <EditionHeader
                  editionId={ editionWithSessions.id }
                  code={ editionWithSessions.editionCode }
                  location={ editionWithSessions.location }
                  isVirtual={ isVirtual }
                  theme={ theme }
                  t={ t }
                />
                <Typography fontWeight={ 700 } sx={ { color: theme.customColors.textPrimary } }>
                  { editionTitle }
                </Typography>
                <Typography  sx={ { color: theme.customColors.textPrimary, fontSize: "0.75rem", mt: 0.5 } }>
                  { dateRange }
                </Typography>
              </Box>
            </Box>
            
            <Box sx={ { alignItems: "flex-end", display: "flex", flexDirection: "column" } }>
              <Box sx={ { alignItems: "center", display: "flex" } }>
                <Typography sx={ { color: theme.customColors.textDisabled, fontSize: "0.75rem" } }>
                  { sessions.length } { !hasMultipleSessions ? t("session").toLocaleLowerCase() : t("edition_sessions") }
                </Typography>
              </Box>
              <AvailabilityInfo
                hasAvailablePlaces={ hasAvailablePlaces }
                remainingPlaces={ editionWithSessions.remainingPlaces }
                totalPlaces={ editionWithSessions.totalPlaces }
                theme={ theme }
                t={ t }
              />
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          { sessions.map((session: SessionWithTeachersAndTutors, index: number) => (
            <Box key={ index }>
              { index !== sessions.length && <Divider /> }
              <SessionItem index={ index } totalSessions={ sessions.length } session={ session } isVirtual={ isVirtual } theme={ theme } t={ t } />
            </Box>
          )) }
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
